'use client'

import { createContext, ReactNode, useContext } from 'react'

import { FormMode } from 'shared/types/forms'

export interface DeliveryAddressPort {
  checkDeliveryCoverage: (postalCode: string, city: string) => Promise<boolean>
  onDeliveryAddressSaved: (addressId: string, mode: FormMode) => void
}

export interface InvoiceAddressPort {
  onInvoiceAddressSaved: (addressId: string, mode: FormMode) => void
  onInvoiceAddressRemoved: (addressId: string) => Promise<void>
}

export interface AccountPort {
  onAccountDeleted: () => Promise<void>
}

export interface CustomerPorts {
  useDeliveryAddress: () => DeliveryAddressPort
  useInvoiceAddress: () => InvoiceAddressPort
  useAccount: () => AccountPort
}

const warnMissingProvider = () => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn('Customer ports used outside CustomerPortsProvider.')
  }
}

const fallbackPorts: CustomerPorts = {
  useDeliveryAddress: () => ({
    checkDeliveryCoverage: async () => {
      warnMissingProvider()

      return true
    },
    onDeliveryAddressSaved: () => warnMissingProvider()
  }),
  useInvoiceAddress: () => ({
    onInvoiceAddressSaved: () => warnMissingProvider(),
    onInvoiceAddressRemoved: async () => warnMissingProvider()
  }),
  useAccount: () => ({
    onAccountDeleted: async () => warnMissingProvider()
  })
}

const CustomerPortsContext = createContext<CustomerPorts>(fallbackPorts)

interface CustomerPortsProviderProps {
  ports: CustomerPorts
  children: ReactNode
}

export const CustomerPortsProvider = ({ ports, children }: CustomerPortsProviderProps) => (
  <CustomerPortsContext.Provider value={ports}>{children}</CustomerPortsContext.Provider>
)

export const useDeliveryAddressPort = (): DeliveryAddressPort =>
  useContext(CustomerPortsContext).useDeliveryAddress()

export const useInvoiceAddressPort = (): InvoiceAddressPort =>
  useContext(CustomerPortsContext).useInvoiceAddress()

export const useAccountPort = (): AccountPort =>
  useContext(CustomerPortsContext).useAccount()
